import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { C, SourceTag, ErrorBox, fmtINR, errOf, inputStyle, btn } from './LeadForms';
import { ExportMenu } from './LeadTools';

/**
 * Lead analytics (PRD screen 8) — /leads/analytics. Server: GET /api/leads/reports/analytics and /daily.
 * Period chips This week / This month / Last month / Custom; KPI cards; leads by stage (tap a bar →
 * the list filtered to that stage); funnel; by source; by staff (the incentive extract columns);
 * by store for managers / HO (overdue tasks first). "Daily counts" = leads per day × store × source,
 * for the two-week parallel run with LSQ.
 */
const istToday = () => new Date(Date.now() + 330 * 60000).toISOString().slice(0, 10);
const addDays = (day, n) => new Date(new Date(`${day}T00:00:00Z`).getTime() + n * 86400000).toISOString().slice(0, 10);
function periodFor(key, custom) {
  const t = istToday();
  if (key === 'week') { const dow = (new Date(`${t}T00:00:00Z`).getUTCDay() + 6) % 7; return { from: addDays(t, -dow), to: t }; }
  if (key === 'last') { const first = `${t.slice(0, 8)}01`; const end = addDays(first, -1); return { from: `${end.slice(0, 8)}01`, to: end }; }
  if (key === 'custom' && custom.from && custom.to) return custom;
  return { from: `${t.slice(0, 8)}01`, to: t };
}
const card = { background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12 };
const th = { textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.4px', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' };
const td = { padding: '8px 10px', fontSize: 12.5, color: C.text, borderBottom: '1px solid #f4ede4', whiteSpace: 'nowrap' };
const pill = (on) => ({ padding: '6px 14px', borderRadius: 18, border: `1px solid ${on ? C.brand : C.border}`, background: on ? C.brand : '#fff', color: on ? '#fff' : C.muted, fontWeight: 700, fontSize: 12.5, cursor: 'pointer' });
const pctText = (v) => (v == null ? '—' : `${v}%`);

function Kpi({ label, value, sub, color = C.text }) {
  return (
    <div style={{ ...card, padding: '12px 14px', minWidth: 150, flex: '1 1 150px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color, marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: C.faint, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function LeadAnalytics({ storeId, stores, onStoreChange }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [pkey, setPkey] = useState('month');
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [source, setSource] = useState('');
  const [data, setData] = useState(null);
  const [daily, setDaily] = useState(null);
  const [error, setError] = useState('');
  const period = useMemo(() => periodFor(pkey, custom), [pkey, custom]);
  const allStores = storeId === 'all';

  useEffect(() => {
    if (!storeId) return;
    setError(''); setData(null);
    api.get('/leads/reports/analytics', { params: { storeId, ...period, source } }).then(r => setData(r.data)).catch(e => setError(errOf(e, 'Could not load analytics')));
  }, [storeId, period, source]);
  useEffect(() => {
    if (!storeId || tab !== 'daily') return;
    setDaily(null);
    api.get('/leads/reports/daily', { params: { storeId, ...period } }).then(r => setDaily(r.data)).catch(e => setError(errOf(e, 'Could not load daily counts')));
  }, [storeId, period, tab]);

  const openStage = (code) => {
    const qs = new URLSearchParams({ stages: code, created: 'custom', from: period.from, to: period.to, ...(source ? { sources: source } : {}) });
    navigate(`/leads?${qs.toString()}`);
  };
  const k = data?.kpis;
  const maxStage = Math.max(1, ...(data?.stages || []).map(s => s.count));
  const funnelMax = Math.max(1, data?.funnel?.[0]?.count || 0);

  // Daily counts pivot: rows = day × store, columns = sources.
  const dailyPivot = useMemo(() => {
    if (!daily) return null;
    const srcs = [...new Set(daily.rows.map(r => r.source))].sort();
    const map = new Map();
    daily.rows.forEach(r => { const key = `${r.day}|${r.store_id}`; if (!map.has(key)) map.set(key, { day: r.day, store: r.store_name, by: {} }); map.get(key).by[r.source] = r.n; });
    return { srcs, rows: [...map.values()] };
  }, [daily]);

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <button onClick={() => navigate('/leads')} style={btn('secondary')}>‹ Leads</button>
        <h2 style={{ margin: 0, fontSize: 20, color: C.text, fontWeight: 800 }}>Lead analytics</h2>
        {stores && (
          <select value={storeId} onChange={e => onStoreChange(e.target.value)} style={{ ...inputStyle, width: 'auto', maxWidth: 240, fontWeight: 600 }}>
            <option value="all">All Stores</option>
            {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
        <div style={{ flex: 1 }} />
        <ExportMenu params={{ storeId, created: 'custom', from: period.from, to: period.to, ...(source ? { sources: source } : {}) }} period={period} onError={setError} />
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        {[['week', 'This week'], ['month', 'This month'], ['last', 'Last month'], ['custom', 'Custom']].map(([kk, l]) => <button key={kk} onClick={() => setPkey(kk)} style={pill(pkey === kk)}>{l}</button>)}
        {pkey === 'custom' && (<>
          <input type="date" value={custom.from} onChange={e => setCustom(c => ({ ...c, from: e.target.value }))} style={{ ...inputStyle, width: 'auto' }} />
          <input type="date" value={custom.to} onChange={e => setCustom(c => ({ ...c, to: e.target.value }))} style={{ ...inputStyle, width: 'auto' }} />
        </>)}
        <select value={source} onChange={e => setSource(e.target.value)} style={{ ...inputStyle, width: 'auto' }}>
          <option value="">All sources</option>
          {['Store walk-in', 'Sage – Find my store', 'Website – Connect with store', 'Pop-ins video call'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <span style={{ fontSize: 12, color: C.faint }}>{period.from} → {period.to}</span>
        <div style={{ flex: 1 }} />
        {[['overview', 'Overview'], ['daily', 'Daily counts']].map(([kk, l]) => <button key={kk} onClick={() => setTab(kk)} style={pill(tab === kk)}>{l}</button>)}
      </div>
      <ErrorBox text={error} />

      {tab === 'daily' ? (
        <div style={{ ...card, overflowX: 'auto' }}>
          <div style={{ padding: '10px 14px', fontSize: 12.5, color: C.muted, borderBottom: `1px solid ${C.border}` }}>Leads created per day, by store and source — compare with LSQ's counts during the two-week parallel run.</div>
          {!dailyPivot ? <div style={{ padding: 30, textAlign: 'center', color: C.faint }}>Loading…</div> : dailyPivot.rows.length === 0 ? <div style={{ padding: 30, textAlign: 'center', color: C.faint }}>No leads in this period.</div> : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>Date</th><th style={th}>Store</th>{dailyPivot.srcs.map(s => <th key={s} style={th}><SourceTag source={s} /></th>)}<th style={th}>Total</th></tr></thead>
              <tbody>{dailyPivot.rows.map(r => (
                <tr key={`${r.day}|${r.store}`}><td style={td}>{r.day}</td><td style={td}>{r.store}</td>
                  {dailyPivot.srcs.map(s => <td key={s} style={td}>{r.by[s] || 0}</td>)}
                  <td style={{ ...td, fontWeight: 700 }}>{Object.values(r.by).reduce((a, b) => a + b, 0)}</td></tr>
              ))}</tbody>
            </table>
          )}
        </div>
      ) : !data ? (!error && <div style={{ padding: 40, textAlign: 'center', color: C.faint }}>Loading…</div>) : (<>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
          <Kpi label="Leads received" value={k.received} sub={`${data.openCount} still open`} />
          <Kpi label="Actioned in 24 h" value={pctText(k.actionedPct)} sub={`${k.actioned} of ${k.received}`} color={k.actionedPct >= 90 ? '#15803d' : k.actionedPct >= 60 ? '#b45309' : '#b91c1c'} />
          <Kpi label="Avg first response" value={k.avgFirstResponseHours == null ? '—' : `${k.avgFirstResponseHours} h`} sub="Sage / website / Pop-ins · target ≤ 2 h" color={k.avgFirstResponseHours != null && k.avgFirstResponseHours <= 2 ? '#15803d' : C.text} />
          <Kpi label="Converted" value={k.customers} sub={`${pctText(k.conversionPct)} · ${fmtINR(k.revenue) || '₹0'}`} color="#15803d" />
          <Kpi label="Overdue tasks now" value={k.overdueTasks} color={k.overdueTasks ? '#b91c1c' : C.text} />
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <div style={{ ...card, flex: '1 1 420px', padding: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: C.text, marginBottom: 10 }}>Leads by stage <span style={{ fontWeight: 400, color: C.faint, fontSize: 12 }}>· tap a bar to open the list</span></div>
            {data.stages.filter(s => s.group !== 'closed').concat([{ divider: true }]).concat(data.stages.filter(s => s.group === 'closed')).map((s, i) => s.divider ? <div key="d" style={{ borderTop: `1px dashed ${C.border}`, margin: '8px 0' }} /> : (
              <div key={s.code} onClick={() => openStage(s.code)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', cursor: 'pointer', opacity: s.group === 'closed' ? 0.6 : 1 }} title={`Open ${s.label} leads`}>
                <div style={{ width: 150, fontSize: 12.5, color: C.text }}>{s.label}</div>
                <div style={{ flex: 1, background: '#f6efe7', borderRadius: 6, height: 16 }}>
                  <div style={{ width: `${(s.count / maxStage) * 100}%`, minWidth: s.count ? 4 : 0, height: 16, borderRadius: 6, background: s.group === 'won' ? '#16a34a' : s.group === 'closed' ? '#9ca3af' : C.brand }} />
                </div>
                <div style={{ width: 40, textAlign: 'right', fontSize: 12.5, fontWeight: 700 }}>{s.count}</div>
              </div>
            ))}
          </div>
          <div style={{ ...card, flex: '1 1 340px', padding: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: C.text, marginBottom: 10 }}>Funnel</div>
            {data.funnel.map(f => (
              <div key={f.key} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', fontSize: 12.5 }}><span style={{ flex: 1 }}>{f.label}</span><b>{f.count}</b><span style={{ width: 54, textAlign: 'right', color: C.faint }}>{data.funnel[0].count ? `${Math.round((f.count / data.funnel[0].count) * 100)}%` : ''}</span></div>
                <div style={{ height: 10, background: '#f6efe7', borderRadius: 5, marginTop: 3 }}><div style={{ width: `${(f.count / funnelMax) * 100}%`, height: 10, borderRadius: 5, background: f.key === 'customer' ? '#16a34a' : '#6366f1' }} /></div>
              </div>
            ))}
            <div style={{ fontSize: 11, color: C.faint, marginTop: 6 }}>Contacted = a connected call. Visit booked / visited = a Store Visit task / one completed as Done. Walk-ins count as contacted and visited.</div>
          </div>
        </div>
        <Table title="By source" cols={[['source', 'Source', v => <SourceTag source={v} />], ['received', 'Received'], ['customers', 'Customers'], ['conversionPct', 'Conversion', pctText], ['avgFirstResponseHours', 'Avg first response', v => (v == null ? '—' : `${v} h`)], ['revenue', 'Revenue', v => fmtINR(v) || '₹0']]} rows={data.bySource} />
        {allStores && <Table title="By store (overdue tasks first)" cols={[['storeName', 'Store'], ['overdueTasks', 'Overdue now'], ['received', 'Received'], ['actionedPct', 'Actioned 24 h', pctText], ['avgFirstResponseHours', 'Avg first response', v => (v == null ? '—' : `${v} h`)], ['customers', 'Customers'], ['conversionPct', 'Conversion', pctText], ['revenue', 'Revenue', v => fmtINR(v) || '₹0']]} rows={data.byStore} onRow={(r) => onStoreChange && onStoreChange(r.storeId)} />}
        <Table title="By staff (incentive extract columns)" cols={[...(allStores ? [['storeName', 'Store']] : []), ['staff', 'Staff login', v => v || <span style={{ color: C.faint }}>Unassigned</span>], ['received', 'Leads owned'], ['actionedPct', 'Actioned 24 h', pctText],
          ['followupCompliancePct', 'Follow-ups on time', (v, r) => (r.followupsDue ? `${v}% (${r.followupsOnTime}/${r.followupsDue})` : '—')], ['visitsBooked', 'Visits booked'], ['visitsHonoured', 'Visits honoured'], ['customers', 'Customers'], ['revenue', 'Revenue', v => fmtINR(v) || '₹0'], ['walkIns', 'Walk-ins logged']]} rows={data.byStaff} />
      </>)}
    </div>
  );
}

function Table({ title, cols, rows, onRow }) {
  return (
    <div style={{ ...card, marginBottom: 12, overflowX: 'auto' }}>
      <div style={{ padding: '10px 14px', fontSize: 13, fontWeight: 800, color: C.text, borderBottom: `1px solid ${C.border}` }}>{title}</div>
      {rows.length === 0 ? <div style={{ padding: 14, fontSize: 12.5, color: C.faint }}>No leads in this period.</div> : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>{cols.map(([kk, l]) => <th key={kk} style={th}>{l}</th>)}</tr></thead>
          <tbody>{rows.map((r, i) => (
            <tr key={i} onClick={onRow ? () => onRow(r) : undefined} style={{ cursor: onRow ? 'pointer' : 'default' }}>
              {cols.map(([kk, , fmt]) => <td key={kk} style={td}>{fmt ? fmt(r[kk], r) : r[kk]}</td>)}
            </tr>
          ))}</tbody>
        </table>
      )}
    </div>
  );
}
